import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_NINJA } from '../../settings';
import { StatusEnum } from '../common-types';

export interface ICategory {
  id: number;
  owner: number;
  weight?: number;
  category: string;
  is_active: boolean;
  added?: string;
}

export interface ICategoryGroup {
  id: number;
  project: number;
  name: string;
  description: string;
  is_active: boolean;
  categories: ICategory[];
  created: string;
  changed: string;
  pages: number;
}

export interface INewCategoryGroup {
  project: number;
  name: string;
  description: string;
  is_active: boolean;
  new_categories: string[];
}

export interface IUpdateCategoryGroup {
  id: number;
  name?: string;
  description?: string;
  is_active?: boolean;
  new_categories?: string[];
  pages?: number;
}

export interface ICategoryGroupsState {
  categoryGroups: ICategoryGroup[];
  currentCategoryGroup: ICategoryGroup | null;
  status: StatusEnum;
  statusText: string | null;
}

const initialState: ICategoryGroupsState = {
  categoryGroups: [],
  currentCategoryGroup: null,
  status: StatusEnum.idle,
  statusText: null,
};

export const getCategoryGroups = createAsyncThunk(
  'organization/getCategoryGroups',
  async (args: { slug: string; clientId?: number; projectId?: number }) => {
    const params: { client?: number; project?: number } = {};
    if (args.clientId) {
      params.client = args.clientId;
    }
    if (args.projectId) {
      params.project = args.projectId;
    }
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/category_groups`,
      { params }
    );
    return response.data as ICategoryGroup[];
  }
);

export const getCategoryGroup = createAsyncThunk(
  'organization/getCategoryGroup',
  async (args: {
    slug: string;
    categoryGroupId: number;
    clientId?: number;
    projectId?: number;
  }) => {
    const params: { client?: number; project?: number } = {};
    if (args.clientId) {
      params.client = args.clientId;
    }
    if (args.projectId) {
      params.project = args.projectId;
    }
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/category_groups/${args.categoryGroupId}`,
      { params }
    );
    return response.data as ICategoryGroup;
  }
);

export const createCategoryGroup = createAsyncThunk(
  'organization/createCategoryGroup',
  async (args: { slug: string; categoryGroup: INewCategoryGroup }) => {
    const response = await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/category_groups`,
      args.categoryGroup
    );
    return response.data as ICategoryGroup;
  }
);

export const updateCategoryGroup = createAsyncThunk(
  'organization/updateCategoryGroup',
  async (args: { slug: string; categoryGroup: IUpdateCategoryGroup }) => {
    const response = await axios.patch(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/category_groups/${args.categoryGroup.id}`,
      args.categoryGroup
    );
    return response.data as ICategoryGroup;
  }
);

export const deleteCategoryGroup = createAsyncThunk(
  'organization/deleteCategoryGroup',
  async (args: { slug: string; categoryGroupId: number }) => {
    await axios.delete(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/category_groups/${args.categoryGroupId}`
    );
    return args.categoryGroupId;
  }
);

export const updateCategory = createAsyncThunk(
  'organization/updateCategory',
  async (args: {
    slug: string;
    categoryGroupId: number;
    categoryId: number;
    data: { category?: string; weight?: number; is_active?: boolean };
  }) => {
    const response = await axios.patch(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/category_groups/${args.categoryGroupId}/categories/${args.categoryId}`,
      args.data
    );
    return {
      categoryGroupId: args.categoryGroupId,
      category: response.data as ICategory,
    };
  }
);

const categoryGroupsSlice = createSlice({
  name: 'categoryGroups',
  initialState,
  reducers: {
    setCategoryGroup: (state, action: PayloadAction<ICategoryGroup | null>) => {
      state.currentCategoryGroup = action.payload;
    },
    setCategoryGroupIdle: (state) => {
      state.status = StatusEnum.idle;
      state.statusText = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCategoryGroups.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        getCategoryGroups.fulfilled,
        (state, action: PayloadAction<ICategoryGroup[]>) => {
          state.status = StatusEnum.succeeded;
          state.categoryGroups = action.payload;
          if (!state.currentCategoryGroup) {
            state.currentCategoryGroup = action.payload[0] ?? null;
          }
        }
      )
      .addCase(getCategoryGroups.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(getCategoryGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        getCategoryGroup.fulfilled,
        (state, action: PayloadAction<ICategoryGroup>) => {
          state.status = StatusEnum.succeeded;
          state.currentCategoryGroup = action.payload;
        }
      )
      .addCase(getCategoryGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(createCategoryGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        createCategoryGroup.fulfilled,
        (state, action: PayloadAction<ICategoryGroup>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Группа категорий создана';
          state.currentCategoryGroup = action.payload;
          state.categoryGroups.push(action.payload);
          toast.success(state.statusText);
        }
      )
      .addCase(createCategoryGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(updateCategoryGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        updateCategoryGroup.fulfilled,
        (state, action: PayloadAction<ICategoryGroup>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Группа категорий обновлена';
          const updatedCategoryGroupIndex = state.categoryGroups.findIndex(
            (c) => c.id === action.payload.id
          );
          if (updatedCategoryGroupIndex !== -1) {
            state.categoryGroups[updatedCategoryGroupIndex] = action.payload;
          } else {
            state.currentCategoryGroup = null;
          }
          toast.success(state.statusText);
        }
      )
      .addCase(updateCategoryGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(deleteCategoryGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        deleteCategoryGroup.fulfilled,
        (state, action: PayloadAction<number | undefined>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Группа категорий удалена';
          if (typeof action.payload !== 'undefined') {
            const deletedCategoryGroupIndex = state.categoryGroups.findIndex(
              (c) => c.id === action.payload
            );
            if (deletedCategoryGroupIndex >= 0) {
              state.categoryGroups.splice(deletedCategoryGroupIndex, 1);
            }
          }
          state.currentCategoryGroup = null;
          toast.success(state.statusText);
        }
      )
      .addCase(deleteCategoryGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(updateCategory.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        updateCategory.fulfilled,
        (
          state,
          action: PayloadAction<{
            categoryGroupId: number;
            category: ICategory;
          }>
        ) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Категория обновлена';
          const groupIndex = state.categoryGroups.findIndex(
            (g) => g.id === action.payload.categoryGroupId
          );
          if (groupIndex !== -1) {
            const group = state.categoryGroups[groupIndex];
            const categories = group?.categories;
            if (categories) {
              const categoryIndex = categories.findIndex(
                (c) => c?.id === action?.payload?.category?.id
              );
              if (categoryIndex !== -1 && categoryIndex !== undefined) {
                categories[categoryIndex] = action.payload.category;
              }
            }
          }
          toast.success(state.statusText);
        }
      )
      .addCase(updateCategory.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      });
  },
});

export const { setCategoryGroup, setCategoryGroupIdle } =
  categoryGroupsSlice.actions;

export default categoryGroupsSlice.reducer;
